import type { AdminGigIssueListItemResponse } from "../../../types/adminTypes";
import { formatUsdFromCents } from "../../../utils/CurrencyUtils";
import { formatDateTime } from "../../../utils/DateUtils";
import Button from "../../ui/Button/Button";
import AdminPillBadge from "../ui/AdminPillBadge";

type AdminIssuesTableRowProps = {
  issue: AdminGigIssueListItemResponse;
  onView: (issue: AdminGigIssueListItemResponse) => void;
};

const statusTone: Record<AdminGigIssueListItemResponse["status"], "warning" | "success" | "danger"> = {
  OPEN: "warning",
  UNDER_REVIEW: "warning",
  AWAITING_SETTLEMENT_APPROVAL: "warning",
  RESOLVED_RELEASE_PAYOUT: "success",
  RESOLVED_SETTLEMENT: "success",
  RESOLVED_REFUND_ADULT: "danger",
  RESOLVED_REFUND_ADULT_WITH_LATE_FEE: "danger",
  RESOLVED_PREPARATION_PAYOUT_AND_REFUND: "success",
  RESOLVED_TEEN_REMOVED_KEEP_FUNDED: "success",
};

const statusLabel: Record<AdminGigIssueListItemResponse["status"], string> = {
  OPEN: "Open",
  UNDER_REVIEW: "Under Review",
  AWAITING_SETTLEMENT_APPROVAL: "Awaiting Settlement Approval",
  RESOLVED_RELEASE_PAYOUT: "Resolved: Payout Released",
  RESOLVED_SETTLEMENT: "Resolved: Settlement Applied",
  RESOLVED_REFUND_ADULT: "Resolved: Refunded",
  RESOLVED_REFUND_ADULT_WITH_LATE_FEE: "Resolved: Refunded Less Fee",
  RESOLVED_PREPARATION_PAYOUT_AND_REFUND: "Resolved: Preparation Payout",
  RESOLVED_TEEN_REMOVED_KEEP_FUNDED: "Resolved: Teen Removed, Funds Held",
};

const issueScopeLabel: Record<AdminGigIssueListItemResponse["issueScope"], string> = {
  TEEN_ASSIGNMENT: "Teen assignment",
  WHOLE_GIG: "Whole gig",
};

const AdminIssuesTableRow = ({ issue, onView }: AdminIssuesTableRowProps) => {
  return (
    <tr className="align-top">
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <div className="space-y-1">
          <p className="font-semibold">{issue.gigTitle ?? "Untitled gig"}</p>
          <p className="line-clamp-2 text-myteengig-text-muted">{issue.messagePreview || "No issue details provided."}</p>
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <div className="space-y-2">
          <AdminPillBadge>{issue.reportedByRole}</AdminPillBadge>
          <AdminPillBadge>{issueScopeLabel[issue.issueScope]}</AdminPillBadge>
          <p className="font-medium">{issue.reportedByName}</p>
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <div className="space-y-1">
          <p>Teen: {issue.teenName}</p>
          <p className="text-myteengig-text-muted">Adult: {issue.adultName}</p>
          {issue.coveredSeats ? <p className="text-myteengig-text-muted">Seats: {issue.coveredSeats}</p> : null}
          {issue.gigFundingAssignmentId ? (
            <p className="break-all text-xs text-myteengig-text-muted">Assignment: {issue.gigFundingAssignmentId}</p>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <div className="space-y-2">
          <AdminPillBadge tone={statusTone[issue.status]}>{statusLabel[issue.status]}</AdminPillBadge>
          {issue.paymentStatus ? <p className="text-xs text-myteengig-text-muted">Payment: {issue.paymentStatus}</p> : null}
          {issue.allocatedPosterChargeCents !== null ? (
            <p className="text-xs text-myteengig-text-muted">
              Allocation: {formatUsdFromCents(issue.allocatedPosterChargeCents)}
            </p>
          ) : null}
          {issue.resolvedAt ? <p className="text-xs text-myteengig-text-muted">Resolved {formatDateTime(issue.resolvedAt)}</p> : null}
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">{formatDateTime(issue.createdAt)}</td>
      <td className="px-4 py-4 text-sm text-myteengig-text-primary">
        <Button variant="outline" size="sm" onClick={() => onView(issue)}>
          View
        </Button>
      </td>
    </tr>
  );
};

export default AdminIssuesTableRow;
